import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import CampaignBanner from './components/vacation/CampaignBanner';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import LoadingSpinner from './components/common/LoadingSpinner';
import { useAuth } from './context/AuthContext';
import { CookieConsentProvider } from './context/CookieConsentContext';
import { OnboardingRedirect } from './components/routing/OnboardingRedirect';
import { OnboardingRoute } from './components/routing/OnboardingRoute';
import { RequireRole } from './components/routing/RequireRole';

// Lazy-loaded pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const HomePageEditorial = lazy(() => import('./pages/HomePageEditorial'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const RegisterPageEditorial = lazy(() => import('./pages/RegisterPageEditorial'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const SearchPageEditorial = lazy(() => import('./pages/SearchPageEditorial'));
const NannyProfilePage = lazy(() => import('./pages/NannyProfilePage'));
const NannyProfilePageEditorial = lazy(() => import('./pages/NannyProfilePageEditorial'));
const ProfilePageEditorial = lazy(() => import('./pages/ProfilePageEditorial'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DashboardPageEditorial = lazy(() => import('./pages/DashboardPageEditorial'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));
const MessagingPageEditorial = lazy(() => import('./pages/MessagingPageEditorial'));
const CredentialMarketplacePage = lazy(() => import('./pages/CredentialMarketplacePage'));
const CredentialDetailPage = lazy(() => import('./pages/CredentialDetailPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PressPage = lazy(() => import('./pages/PressPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const CommunityGuidelinesPage = lazy(() => import('./pages/CommunityGuidelinesPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const SafetyCenterPage = lazy(() => import('./pages/SafetyCenterPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const VacationCoverageSearchPage = lazy(() => import('./pages/VacationCoverageSearchPage'));
const RecommendationSubmitPage = lazy(() => import('./pages/RecommendationSubmitPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const NannyOnboardingPageEditorial = lazy(() => import('./pages/NannyOnboardingPageEditorial'));
const ParentOnboardingPage = lazy(() => import('./pages/ParentOnboardingPage'));
const ParentOnboardingPageEditorial = lazy(() => import('./pages/ParentOnboardingPageEditorial'));
const ParentProfileLandingPage = lazy(() => import('./pages/ParentProfileLandingPage'));

const homepageVariant = (() => {
  const raw = import.meta.env.VITE_HOMEPAGE_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  if (raw !== undefined) {
    console.warn(`Unknown VITE_HOMEPAGE_VARIANT value: ${raw}. Falling back to 'legacy'.`);
  }
  return 'legacy';
})();

const searchVariant = (() => {
  const raw = import.meta.env.VITE_SEARCH_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  if (raw !== undefined) {
    console.warn(`Unknown VITE_SEARCH_VARIANT value: ${raw}. Falling back to 'legacy'.`);
  }
  return 'legacy';
})();

const nannyProfileVariant = (() => {
  const raw = import.meta.env.VITE_NANNY_PROFILE_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  if (raw !== undefined) {
    console.warn(`Unknown VITE_NANNY_PROFILE_VARIANT value: ${raw}. Falling back to 'legacy'.`);
  }
  return 'legacy';
})();

const parentOnboardingVariant = (() => {
  const raw = import.meta.env.VITE_PARENT_ONBOARDING_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  if (raw !== undefined) {
    console.warn(`Unknown VITE_PARENT_ONBOARDING_VARIANT value: ${raw}. Falling back to default.`);
  }
  return import.meta.env.DEV ? 'editorial' : 'legacy';
})();

const nannyOnboardingVariant = (() => {
  const raw = import.meta.env.VITE_NANNY_ONBOARDING_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  // Off by default: fall back to 'legacy', but never silently (per #727) -- warn
  // on both an unset and an unexpected value so a misconfigured environment is
  // visible rather than quietly shipping the legacy variant.
  console.warn(
    `VITE_NANNY_ONBOARDING_VARIANT is ${
      raw === undefined ? 'unset' : `an unknown value: ${raw}`
    }. Falling back to 'legacy'.`,
  );
  return 'legacy';
})();

const messagingVariant = (() => {
  const raw = import.meta.env.VITE_MESSAGING_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  // Off by default: fall back to 'legacy', but never silently (per #727) -- warn
  // on both an unset and an unexpected value so a misconfigured environment is
  // visible rather than quietly shipping the legacy variant.
  console.warn(
    `VITE_MESSAGING_VARIANT is ${
      raw === undefined ? 'unset' : `an unknown value: ${raw}`
    }. Falling back to 'legacy'.`,
  );
  return 'legacy';
})();

const dashboardVariant = (() => {
  const raw = import.meta.env.VITE_DASHBOARD_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  // Off by default: fall back to 'legacy', but never silently (per #727) -- warn
  // on both an unset and an unexpected value so a misconfigured environment is
  // visible rather than quietly shipping the legacy variant.
  console.warn(
    `VITE_DASHBOARD_VARIANT is ${
      raw === undefined ? 'unset' : `an unknown value: ${raw}`
    }. Falling back to 'legacy'.`,
  );
  return 'legacy';
})();

const registerVariant = (() => {
  const raw = import.meta.env.VITE_REGISTER_VARIANT;
  if (raw === 'editorial' || raw === 'legacy') return raw;
  // Off by default: fall back to 'legacy', but never silently (per #727) -- warn
  // on both an unset and an unexpected value so a misconfigured environment is
  // visible rather than quietly shipping the legacy variant.
  console.warn(
    `VITE_REGISTER_VARIANT is ${
      raw === undefined ? 'unset' : `an unknown value: ${raw}`
    }. Falling back to 'legacy'.`,
  );
  return 'legacy';
})();

// Protected route wrapper — redirects to login with return URL
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = window.location.pathname;

  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) {
    const redirect = location !== '/dashboard' ? `?redirect=${encodeURIComponent(location)}` : '';
    return <Navigate to={`/login${redirect}`} replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const hideSharedChrome =
    // /profile migrated in place to the editorial shell (no flag, #1241); it
    // renders its own Masthead/ProfileFooter, so hide the shared Header/Footer.
    location.pathname === '/profile' ||
    (homepageVariant === 'editorial' && location.pathname === '/') ||
    (searchVariant === 'editorial' && location.pathname === '/search') ||
    (nannyProfileVariant === 'editorial' && location.pathname.startsWith('/nanny/')) ||
    (parentOnboardingVariant === 'editorial' && location.pathname === '/onboarding/parent') ||
    (nannyOnboardingVariant === 'editorial' && location.pathname === '/onboarding/nanny') ||
    (messagingVariant === 'editorial' &&
      (location.pathname === '/messages' || location.pathname.startsWith('/messages/'))) ||
    // Nanny-only: parents fall back to the legacy DashboardPage inside
    // DashboardPageEditorial and still need the shared Header/Footer (#1180).
    (dashboardVariant === 'editorial' &&
      location.pathname === '/dashboard' &&
      user?.role === 'nanny');

  return (
    <CookieConsentProvider>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('common.appName')} - {t('common.tagline')}</title>
        <meta name="description" content={t('home.heroSubtitle')} />
      </Helmet>

      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="skip-link">
        {t('accessibility.skipToContent')}
      </a>

      <div className="flex flex-col min-h-screen">
        <CampaignBanner />
        {!hideSharedChrome && <Header />}

        <main id="main-content" className="flex-1" role="main">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              <Route
                path="/"
                element={homepageVariant === 'editorial' ? <HomePageEditorial /> : <HomePage />}
              />
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/register"
                element={registerVariant === 'editorial' ? <RegisterPageEditorial /> : <RegisterPage />}
              />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route
                path="/search"
                element={
                  <RequireRole allow="parent">
                    {searchVariant === 'editorial' ? <SearchPageEditorial /> : <SearchPage />}
                  </RequireRole>
                }
              />
              <Route
                path="/nanny/:id"
                element={
                  nannyProfileVariant === 'editorial' ? (
                    <NannyProfilePageEditorial />
                  ) : (
                    <NannyProfilePage />
                  )
                }
              />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/community" element={<CommunityGuidelinesPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/safety" element={<SafetyCenterPage />} />

              {/* Nanny onboarding */}
              <Route
                path="/onboarding/nanny"
                element={
                  <RequireRole allow="nanny">
                    <OnboardingRoute>
                      {nannyOnboardingVariant === 'editorial' ? (
                        <NannyOnboardingPageEditorial />
                      ) : (
                        <OnboardingPage />
                      )}
                    </OnboardingRoute>
                  </RequireRole>
                }
              />
              <Route path="/onboarding" element={<OnboardingRedirect />} />

              {/* Parent onboarding (PO-2: shell) */}
              <Route
                path="/onboarding/parent"
                element={
                  <RequireRole allow="parent">
                    <OnboardingRoute>
                      {parentOnboardingVariant === 'editorial' ? (
                        <ParentOnboardingPageEditorial />
                      ) : (
                        <ParentOnboardingPage />
                      )}
                    </OnboardingRoute>
                  </RequireRole>
                }
              />
              <Route
                path="/onboarding/parent/profile"
                element={
                  <RequireRole allow="parent">
                    <OnboardingRoute>
                      <ParentProfileLandingPage />
                    </OnboardingRoute>
                  </RequireRole>
                }
              />

              {/* Protected routes */}
              <Route
                path="/dashboard"
                element={
                  <RequireRole allow={['parent', 'nanny']}>
                    {dashboardVariant === 'editorial' ? (
                      <DashboardPageEditorial />
                    ) : (
                      <DashboardPage />
                    )}
                  </RequireRole>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireRole allow={['parent', 'nanny']}>
                    <ProfilePageEditorial />
                  </RequireRole>
                }
              />
              <Route
                path="/messages"
                element={
                  <ProtectedRoute>
                    {messagingVariant === 'editorial' ? (
                      <MessagingPageEditorial />
                    ) : (
                      <MessagingPage />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages/:threadId"
                element={
                  <ProtectedRoute>
                    {messagingVariant === 'editorial' ? (
                      <MessagingPageEditorial />
                    ) : (
                      <MessagingPage />
                    )}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/credentials"
                element={
                  <RequireRole allow="nanny">
                    <CredentialMarketplacePage />
                  </RequireRole>
                }
              />
              <Route
                path="/credentials/:catalogId"
                element={
                  <RequireRole allow="nanny">
                    <CredentialDetailPage />
                  </RequireRole>
                }
              />
              <Route
                path="/coverage"
                element={
                  <RequireRole allow={['parent', 'nanny']}>
                    <VacationCoverageSearchPage />
                  </RequireRole>
                }
              />
              <Route path="/recommend/:token" element={<RecommendationSubmitPage />} />
              <Route
                path="/referrals"
                element={
                  <ProtectedRoute>
                    <ReferralPage />
                  </ProtectedRoute>
                }
              />

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </main>

        {!hideSharedChrome && <Footer />}
        <CookieConsentBanner />
      </div>
    </CookieConsentProvider>
  );
};

export default App;
